import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SnackBarData } from 'src/app/common/components/snackbar/model/snackbar.model';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/common/components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { DirtyCheck } from 'src/app/common/guards/form-deactivate.guard';
import { ShopinfoService } from '../../system/shopinfo/shopinfo.service';
import { WeeklyResultByStoreReportService } from '../weekly-result-by-store-report/weekly-result-by-store-report.service';
import { HqWeeklySalesService } from './weekly-sales.service';
import { DatePipe } from '@angular/common';
import { PermissionService } from 'src/app/auth/permission.service';

@Component({
  selector: 'app-weekly-sales',
  standalone: false,
  templateUrl: './weekly-sales.component.html',
  styleUrl: './weekly-sales.component.scss'
})
export class HqWeeklySalesComponent implements OnInit {

  title: string = "Sales Summary (Weekly)";
  IsDownloading: boolean = false;
  editForm: FormGroup;
  currentYear: number;
  selectedYear: any;
  weeks: any[] = [];
  shopListData: any[] = [];
  filteredShopList: any[] = [];
  weekStartDate: any;
  weekEndDate: any;
  financialYears: any[] = [];
  years: number[] = [];
  curFinYear!: string;
  permission: any;
  weekRangeError: string | null = null;

  constructor(
    private router: Router,
    private builder: FormBuilder,
    private route: ActivatedRoute,
    private weeklySalesService: HqWeeklySalesService,
    private snackBarService: SnackBarService,
    private shopinfoService: ShopinfoService,
    private permissionService: PermissionService,
    public dialog: MatDialog,
    private datePipe: DatePipe
  ) {
    let uomId = this.route.snapshot.paramMap.get('id');
    this.editForm = this.builder.group({
      year: [null, Validators.required],
      week_from: [[], Validators.required],
      week_to: [[], Validators.required],
      // shop_id: ['', [Validators.required, this.shopValidator.bind(this)]]
      shop_id: this.builder.control('ALL', [Validators.required, this.shopValidator.bind(this)]),

    }, { validators: this.weekRangeValidator.bind(this) }); // <-- add validator here

    this.currentYear = new Date().getFullYear();

    this.editForm.valueChanges.subscribe(() => {
      this.saveToSessionStorage();
    });
    this.permission = permissionService.getPagePermission("weekly-sales-summary-hq");

  }

  ngOnInit(): void {
    this.loadData();
    this.shopList();
    this.editForm.get('week_from')?.valueChanges.subscribe((selectedWeekNo: number) => {
      const selectedWeek = this.weeks.find(week => week.week_no === selectedWeekNo);
      if (selectedWeek) {
        this.weekStartDate = selectedWeek.start_date;
        this.editForm.patchValue({
          week_from: selectedWeek.week_no
        }, { emitEvent: false });
      }
      this.editForm.updateValueAndValidity(); // trigger validator
    });
    this.editForm.get('week_to')?.valueChanges.subscribe((selectedWeekNo: number) => {
      const selectedWeek = this.weeks.find(week => week.week_no === selectedWeekNo);
      if (selectedWeek) {
        this.weekEndDate = selectedWeek.end_date;
        this.editForm.patchValue({
          week_to: selectedWeek.week_no
        }, { emitEvent: false });
      }
      this.editForm.updateValueAndValidity(); // trigger validator
    });
  }

  getCurrentWeek(weeks: any[]): any {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return weeks.find(week => {
      const startDate = new Date(week.start_date);
      const endDate = new Date(week.end_date);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      return today >= startDate && today <= endDate;
    });
  }

  shopValidator(control: AbstractControl) {
    if (!control.value) return { invalidShop: true };
    if (control.value === 'ALL') return null; // Allow "ALL"
    if (typeof control.value === 'object' && this.shopListData.some((shop: { id: any; }) => shop.id === control.value.id)) {
      return null;
    }
    if (typeof control.value === 'string' && this.shopListData.some((shop: { name: any; }) => shop.name === control.value)) {
      return null;
    }
    return { invalidShop: true };
  }

  shopList(): void {
    let requestData = { search: { filters: { filter: [], scope: { offset: 0, limit: 1000000 } }, sort: [{ column: "shop.name", order: "ASC" }] } };
    this.weeklySalesService.getShopList(requestData).subscribe({
      next: (response: any) => {
        this.shopListData = response.data.shops;
        this.filteredShopList = response.data.shops;
      },
      error: (error: any) => {
        console.error('Error fetching shops:', error);
      }
    });
  }

  filterShops() {
    const value = this.editForm.get('shop_id')?.value;
    const filterValue = typeof value === 'string' ? value.toLowerCase() : (value?.name?.toLowerCase() || '');
    this.filteredShopList = this.shopListData.filter((shop: { name: string; }) =>
      shop.name.toLowerCase().includes(filterValue)
    );
  }

  displayShop(shop: any): string {
    if (shop === 'ALL') return 'All';
    return shop && shop.name ? shop.name : '';
  }

  clearShop() {
    this.editForm.get('shop_id')?.setValue('');
    this.filteredShopList = this.shopListData.slice();
  }

  sortOptions = [
    { value: 'shop_name', label: 'Shop Name' },
    { value: 'this_year_this_week_sales', label: 'This Year Sales' },
    { value: 'last_year_this_week_sales', label: 'Last Year Sales' },
    { value: 'this_week_cust_orders', label: 'This Year Customer Orders' },
    { value: 'last_year_cus_order', label: 'Last Year Customer Orders' },
    { value: 'this_week_labour_cost', label: 'This Year Labour Cost' },
    { value: 'last_year_this_week_labour_cost', label: 'Last Year Labour Cost' }
  ];

  sortOrderOptions = [
    { value: 'asc', label: 'Ascending' },
    { value: 'desc', label: 'Descending' }
  ];

  saveToSessionStorage(): void {
    const selectedShopId = this.editForm.get('shop_id')?.value;
    const shopId = selectedShopId === 'ALL' ? null : selectedShopId?.id || selectedShopId;
    const reportData = {
      year: this.editForm.get('year')?.value,
      weekFrom: this.editForm.get('week_from')?.value,
      weekTo: this.editForm.get('week_to')?.value,
      shopId: shopId
    };
    sessionStorage.setItem('weeklyBranchReportData', JSON.stringify(reportData));
  }

  loadFromSessionStorage(): void {
    const savedData = sessionStorage.getItem('weeklyBranchReportData');
    if (savedData) {
      const reportData = JSON.parse(savedData);
      this.editForm.patchValue({
        year: reportData.year,
        week_from: reportData.weekFrom,
        week_to: reportData.weekTo,
        shop_id: reportData.shopId
      }, { emitEvent: false });
      if (reportData.year) {
        this.onYearChange();
      }
    }
  }

  onYearChange(): void {
    this.selectedYear = this.editForm.get('year')?.value;
    const selectedFinYear = this.financialYears.find(fy => fy.year === this.selectedYear);
    if (selectedFinYear) {
      let weeks = selectedFinYear.weeks;
      // Filter out future weeks if today is within this financial year
      const today = new Date();
      const isCurrentFinYear = weeks.some((week: any) => {
        const start = new Date(week.start_date);
        const end = new Date(week.end_date);
        return today >= start && today <= end;
      });
      if (isCurrentFinYear) {
        // Include all weeks whose start_date is <= today (so current week is included)
        weeks = weeks.filter((week: any) => new Date(week.start_date) <= today);
      }
      // Sort weeks in descending order by week_no
      this.weeks = weeks
        .slice()
        .sort((a: any, b: any) => b.week_no - a.week_no)
        .map((week: any) => ({
          ...week,
          displayText: `${this.padWeekNumber(week.week_no)} (${this.datePipe.transform(week.start_date, 'dd-MM-yyyy')} - ${this.datePipe.transform(week.end_date, 'dd-MM-yyyy')})`
        }));
      if (this.weeks.length > 0) {
        this.editForm.get('week_from')?.setValue(this.weeks[0].week_no);
        this.editForm.get('week_to')?.setValue(this.weeks[0].week_no);
      }
    } else {
      this.weeks = [];
      this.editForm.get('week_from')?.setValue(null);
      this.editForm.get('week_to')?.setValue(null);
    }
  }

  buildRequestData(): any {
    return {
      search: {
        filters: {
          simpleSearch: [],
          advSearch: [],
          scope: {
            limit: "100",
            offset: "0"
          }
        },
        sort: [
          {
            column: "wd.year",
            order: "desc"
          }
        ]
      }
    };
  }

  loadData(): void {
    let requestData = this.buildRequestData();
    this.weeklySalesService.getWeekNoList(requestData).subscribe(response => {
      if (response.status === 'SUCCESS') {
        this.financialYears = response.data.finYears;
        this.years = this.financialYears.map(fy => fy.year);
        this.curFinYear = response.data.curFinYear;
        const currentYearValue = parseInt(this.curFinYear.split('-')[0]);
        this.financialYears = response.data.finYears.filter((fy: any) =>
          fy.year <= currentYearValue
        );
        this.years = this.financialYears.map(fy => fy.year);
        if (!this.editForm.get('year')?.value) {
          const yearValue = parseInt(this.curFinYear);
          this.editForm.get('year')?.setValue(yearValue, { emitEvent: false });
          const currentFinYear = this.financialYears.find(fy => fy.year === yearValue);
          if (currentFinYear) {
            let weeks = currentFinYear.weeks;
            // Filter out future weeks if today is within this financial year
            const today = new Date();
            const isCurrentFinYear = weeks.some((week: any) => {
              const start = new Date(week.start_date);
              const end = new Date(week.end_date);
              return today >= start && today <= end;
            });
            if (isCurrentFinYear) {
              // Include all weeks whose start_date is <= today (so current week is included)
              weeks = weeks.filter((week: any) => new Date(week.start_date) <= today);
            }
            // Sort weeks in descending order by week_no
            this.weeks = weeks
              .slice()
              .sort((a: any, b: any) => b.week_no - a.week_no)
              .map((week: any) => ({
                ...week,
                displayText: `${this.padWeekNumber(week.week_no)} (${this.datePipe.transform(week.start_date, 'dd-MM-yyyy')} - ${this.datePipe.transform(week.end_date, 'dd-MM-yyyy')})`
              }));
            const currentWeek = this.getCurrentWeek(this.weeks);
            if (currentWeek) {
              this.editForm.get('week_from')?.setValue(currentWeek.week_no);
              this.editForm.get('week_to')?.setValue(currentWeek.week_no);
            } else if (this.weeks.length > 0) {
              this.editForm.get('week_from')?.setValue(this.weeks[0].week_no);
              this.editForm.get('week_to')?.setValue(this.weeks[0].week_no);
            }
          }
        }
      } else {
        console.error('API Error:', response.message);
      }
    });
  }

  padWeekNumber(weekNo: number): string {
    return weekNo.toString().padStart(2, '0');
  }

  onShopSelectionChange(): void {
    this.saveToSessionStorage();
  }

  navigateToWeeklyReport() {
    this.router.navigate(['/weekly-result-branch-report-view']);
  }

  exportClicked(): void {
    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      let errorMsg = 'Please fill all required fields.';
      if (this.editForm.hasError('invalidWeekRange')) {
        errorMsg = this.weekRangeError || 'Invalid week range.';
      }
      let snackBarData: SnackBarData = {
        message: errorMsg,
        title: 'Validation Error',
        type: 'error'
      };
      this.snackBarService.openSnackBar(snackBarData);
      return;
    }

    this.IsDownloading = true;

    const selectedShopId = this.editForm.get('shop_id')?.value;
    const shopId = selectedShopId === 'ALL' ? null : selectedShopId?.id || selectedShopId;

    const reportData = {
      year: this.editForm.get('year')?.value,
      weekFrom: this.editForm.get('week_from')?.value,
      weekTo: this.editForm.get('week_to')?.value,
      shopId: shopId
    };

    this.weeklySalesService.getWeekSales(reportData).subscribe({
      next: (response: Blob) => {
        const reader = new FileReader();

        reader.onload = () => {
          try {
            const text = reader.result as string;
            const json = JSON.parse(text);

            // Check for 204 status (no data found)
            if (json.status === 204 || json.status === 'NO_DATA_FOUND') {
              let snackBarData: SnackBarData = {
                message: 'No data found for the selected criteria.',
                title: 'No Data Found!',
                type: 'warn'
              };
              this.IsDownloading = false;
              this.snackBarService.openSnackBar(snackBarData);
              return;
            }

            // Check for other error statuses
            if (json.status === 'ERROR' || json.status === 'FAILED') {
              let snackBarData: SnackBarData = {
                message: json.message || 'An error occurred while generating the report.',
                title: 'Error!',
                type: 'error'
              };
              this.IsDownloading = false;
              this.snackBarService.openSnackBar(snackBarData);
              return;
            }

            // If we reach here with a JSON response, show warning
            let snackBarData: SnackBarData = {
              message: 'Unexpected response format from server.',
              title: 'Warning!',
              type: 'warn'
            };
            this.IsDownloading = false;
            this.snackBarService.openSnackBar(snackBarData);

          } catch (e) {
            // If JSON parsing fails, it's likely a binary file (Excel)
            // Check if the response is empty or very small
            if (response.size === 0) {
              let snackBarData: SnackBarData = {
                message: 'No data available for export.',
                title: 'No Data Found!',
                type: 'warn'
              };
              this.IsDownloading = false;
              this.snackBarService.openSnackBar(snackBarData);
              return;
            }

            // Check if response is too small to be a valid file
            if (response.size < 100) {
              let snackBarData: SnackBarData = {
                message: 'No data found for the selected criteria.',
                title: 'No Data Found!',
                type: 'warn'
              };
              this.IsDownloading = false;
              this.snackBarService.openSnackBar(snackBarData);
              return;
            }

            // Valid binary file - proceed with download
            const blob = new Blob([response], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'weekly-sales.xlsx';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            this.IsDownloading = false;

            // Show success message
            let snackBarData: SnackBarData = {
              message: 'Report downloaded successfully.',
              title: 'Success!',
              type: 'success'
            };
            this.snackBarService.openSnackBar(snackBarData);
          }
        };

        reader.readAsText(response);
      },
      error: (error) => {
        console.error('Error fetching data:', error);
        this.IsDownloading = false;

        let snackBarData: SnackBarData = {
          message: 'An error occurred while fetching the report. Please try again.',
          title: 'Error!',
          type: 'error'
        };
        this.snackBarService.openSnackBar(snackBarData);
      }
    });
  }

  weekRangeValidator(formGroup: FormGroup) {
    const weekFrom = formGroup.get('week_from')?.value;
    const weekTo = formGroup.get('week_to')?.value;
    if (weekFrom && weekTo && weekTo < weekFrom) {
      this.weekRangeError = 'Week To cannot be less than Week From.';
      return { invalidWeekRange: true };
    }
    this.weekRangeError = null;
    return null;
  }
}